import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";

import { FormBlock, TextInput } from "@/shared/foundation/primitives";

import {
  IMPLICIT_COLLECTION_DESCRIPTION,
  IMPLICIT_COLLECTION_VAR_NAME,
  IMPLICIT_DATASET_DESCRIPTION,
  IMPLICIT_DATASET_VAR_NAME,
  type VariableMeta,
} from "../Agents/buildRequestTemplate";

import { type AgentFormValues } from "./agentFormSchema";
import { KeyValueTable } from "./KeyValueTable";
import { useAgentVariableCatalog } from "./useAgentVariableCatalog";
import styles from "./VariablesBlock.module.css";

const BLANK: VariableMeta = { description: "", default: "" };

/** The fixed meaning of a variable nobody typed, or nothing for one they did. */
function fixedDescription(name: string): string | null {
  if (name === IMPLICIT_DATASET_VAR_NAME) {
    return IMPLICIT_DATASET_DESCRIPTION;
  }
  if (name === IMPLICIT_COLLECTION_VAR_NAME) {
    return IMPLICIT_COLLECTION_DESCRIPTION;
  }
  return null;
}

/**
 * Every variable the agent declares, with what to tell the person launching
 * it.
 *
 * The names are not typed here — they are read out of the text that mentions
 * them — so a row appears the moment a `{{name}}` does. Two of them arrive
 * without being typed at all: the dataset every goal runs on, and the
 * collection a create-events goal files into. Their meanings are invariant, so
 * those rows state theirs instead of asking.
 *
 * A default is not asked for here. One a saved agent already carries stays in
 * the record untouched, so editing the agent does not drop it.
 *
 * The whole record is held by one `Controller` rather than a field per row,
 * because a variable's name is the record's key and two of them carry a dot,
 * which react-hook-form would read as another level of nesting.
 */
export function VariablesBlock() {
  const { control } = useFormContext<AgentFormValues>();
  const catalog = useAgentVariableCatalog();
  const titleId = React.useId();
  const descriptionHeadingId = React.useId();
  const names = catalog.paths.map((entry) => entry.path);

  return (
    <FormBlock
      titleId={titleId}
      title="Variables"
      hint="Rename or delete a placeholder to rename or drop its variable."
      className={styles.block}
      data-testid="agent-variables"
      data-count={names.length}
    >
      <Controller
        control={control}
        name="variableMeta"
        render={({ field }) => {
          const meta = field.value;
          const write = (name: string, patch: Partial<VariableMeta>) => {
            field.onChange({
              ...meta,
              [name]: { ...BLANK, ...meta[name], ...patch },
            });
          };
          return (
            <KeyValueTable
              keyHeading="Variable"
              valueHeading="Description"
              valueHeadingId={descriptionHeadingId}
            >
              {names.length === 0 ? (
                <KeyValueTable.EmptyRow data-testid="agent-variables-empty">
                  No variables yet. Reference one anywhere above by typing{" "}
                  <code>{"{{name}}"}</code>.
                </KeyValueTable.EmptyRow>
              ) : (
                names.map((name) => {
                  const fixed = fixedDescription(name);
                  return (
                    <KeyValueTable.Row
                      key={name}
                      label={name}
                      labelClassName={styles.name}
                      data-testid={`agent-variable-${name}`}
                    >
                      {fixed === null ? (
                        <TextInput
                          aria-labelledby={descriptionHeadingId}
                          placeholder="What this is for, shown to whoever launches the agent."
                          value={meta[name]?.description ?? ""}
                          onChange={(event) => {
                            write(name, {
                              description: event.target.value,
                            });
                          }}
                        />
                      ) : (
                        <p className={styles.fixed}>{fixed}</p>
                      )}
                    </KeyValueTable.Row>
                  );
                })
              )}
            </KeyValueTable>
          );
        }}
      />
    </FormBlock>
  );
}
